import { createBindingStore, createZoomStore, platform, sanitizeNickname, type ActionInfo, type Binding } from '@platoon/client-kit';
import { PROP_COUNT } from '@mayday/shared';

export { sanitizeNickname, type Binding } from '@platoon/client-kit';

export type MoveMode = 'aim' | 'screen';

const MOVE_MODE_KEY = 'moveMode';
const NICKNAME_KEY = 'nickname';
const CAMO_PROP_KEY = 'camoProp';
const BINDINGS_KEY = 'bindings';
const ZOOM_KEY = 'zoom';
const DEBUG_KEY = 'debug';

// Camera zoom: 1 = default. Zooming out goes as far as "the whole level
// fits the screen" (computed by the game scene from level and screen size;
// ZOOM_MIN_ABS is only a sanity floor). Seeing further is not an advantage:
// the server only sends what is within VIEW_RADIUS. See docs/controls.md.
export const ZOOM_MIN_ABS = 0.1;
export const ZOOM_MAX = 1.6;
export const ZOOM_STEP = 1.1;

// ---- key bindings ------------------------------------------------------------
// Actions a player may rebind. Fire (mouse), Tab (scoreboard) and Esc
// (options) are fixed. See docs/screens/menu.md.
export type Action = 'up' | 'down' | 'left' | 'right' | 'walk' | 'reload' | 'switchWeapon' | 'leanLeft' | 'leanRight' | 'jump';

export const ACTIONS: ReadonlyArray<ActionInfo<Action>> = [
  { action: 'up', name: 'Move forward', help: 'Toward the cursor (aim-relative) or up the screen' },
  { action: 'down', name: 'Move back', help: 'Backpedal at 75% speed' },
  { action: 'left', name: 'Move left', help: 'Strafe' },
  { action: 'right', name: 'Move right', help: 'Strafe' },
  { action: 'walk', name: 'Walk', help: 'Hold: slower, much steadier aim. You run by default' },
  { action: 'reload', name: 'Reload', help: 'Also automatic when the clip is empty' },
  { action: 'switchWeapon', name: 'Rifle / grenade', help: 'Switch takes 0.4 s; with the grenade out, fire throws it at the cursor. 1 and 2 pick directly' },
  { action: 'leanLeft', name: 'Lean left', help: 'Not active yet' },
  { action: 'leanRight', name: 'Lean right', help: 'Not active yet' },
  { action: 'jump', name: 'Jump', help: 'Not active yet' },
];

export const DEFAULT_BINDINGS: Readonly<Record<Action, Binding>> = {
  up: { code: 87, label: 'W' },
  down: { code: 83, label: 'S' },
  left: { code: 65, label: 'A' },
  right: { code: 68, label: 'D' },
  walk: { code: 16, label: 'Shift' },
  reload: { code: 82, label: 'R' },
  switchWeapon: { code: 81, label: 'Q' },
  leanLeft: { code: 90, label: 'Z' },
  leanRight: { code: 67, label: 'C' },
  jump: { code: 32, label: 'Space' },
};

// Keys that can never be bound to an action: Tab, Esc, and the zoom keys
// (+ / = / - and the numpad pair).
// ... plus 1 and 2, the fixed weapon slot keys.
export const RESERVED_KEY_CODES: ReadonlySet<number> = new Set([9, 27, 187, 189, 61, 173, 107, 109, 49, 50]);

export const bindingStore = createBindingStore(platform, BINDINGS_KEY, ACTIONS, DEFAULT_BINDINGS, RESERVED_KEY_CODES);
const zoomStore = createZoomStore(platform, ZOOM_KEY, ZOOM_MIN_ABS, ZOOM_MAX);

// Fired on `game.events` when a setting changes; scenes react to it.
export const SETTINGS_CHANGED = 'settings:changed';

// Screen-relative is the default for new players (decided 2026-10-03:
// easier to pick up in a top-down game); a saved choice wins.
function readMoveMode(): MoveMode {
  const v = platform.loadSetting(MOVE_MODE_KEY);
  return v === 'aim' ? 'aim' : 'screen';
}

function readCamoProp(): number {
  const n = Number(platform.loadSetting(CAMO_PROP_KEY) ?? 0);
  return Number.isInteger(n) && n >= 1 && n <= PROP_COUNT ? n : 0;
}

// Mutable, read by the game scene every tick.
export const settings = {
  moveMode: readMoveMode(),
  // The connection/debug line at the bottom of the HUD: on in dev builds
  // unless switched off, off in production unless switched on (F3 or the
  // Controls page).
  showDebug: (platform.loadSetting(DEBUG_KEY) ?? (import.meta.env.DEV ? '1' : '0')) === '1',
  nickname: sanitizeNickname(platform.loadSetting(NICKNAME_KEY) ?? ''),
  camoProp: readCamoProp(), // 0 = never chosen; the server picks at first pickup
  get bindings(): Readonly<Record<Action, Binding>> {
    return bindingStore.bindings;
  },
  get zoom(): number {
    return zoomStore.value;
  },
};

export function setZoom(z: number, min = ZOOM_MIN_ABS): number {
  return zoomStore.set(z, min);
}

export function setBinding(action: Action, binding: Binding): boolean {
  return bindingStore.set(action, binding);
}

export function resetBindings(): void {
  bindingStore.reset();
}

export function setCamoProp(prop: number): void {
  if (prop < 1 || prop > PROP_COUNT) return;
  settings.camoProp = prop;
  platform.saveSetting(CAMO_PROP_KEY, String(prop));
}

export function setShowDebug(on: boolean): void {
  settings.showDebug = on;
  platform.saveSetting(DEBUG_KEY, on ? '1' : '0');
}

export function setMoveMode(mode: MoveMode): void {
  settings.moveMode = mode;
  platform.saveSetting(MOVE_MODE_KEY, mode);
}

export function setNickname(raw: string): string {
  const name = sanitizeNickname(raw);
  settings.nickname = name;
  platform.saveSetting(NICKNAME_KEY, name);
  return name;
}
